import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router';
import { lazyWithRetry } from 'Shared/utils/retry';

// Types
import { OrganizationRole } from 'Shared/constants/types';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody, WpPageTitle } from 'Wpds/components/page-layout';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import MembersManagement from './MembersManagement/MembersManagement';
import Enterprise from './Enterprise/Enterprise';
import BillingPage from './BillingPageV2/BillingPage';
import ExecutiveDashboardV2 from './ExecutiveDashboardV2/ExecutiveDashboardV2';

const NetworkVisualizer = lazyWithRetry(() =>
  import(/* webpackPrefetch: true */ 'Dashboard/components/NetworkVisualizer/NetworkVisualizer')
);

const OrgSettingsPage = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { enterpriseSettings: showIntegrationSettings } = useFlags();
  const isOrganizationAdmin = useSelector(state => state.user.organizationRole === OrganizationRole.admin);
  const { isTrial, isTrialExpired } = useOrganizationSubscription();

  const { hasPi2Access, isStandAlone } = useSmac();
  const { enablePerformOrgManagement } = useFlags();

  const showBillingTab = enablePerformOrgManagement
    ? !isTrial && !isTrialExpired && !isStandAlone && !hasPi2Access
    : !isTrial && !isTrialExpired;

  const tabName = enablePerformOrgManagement && (isStandAlone || hasPi2Access) ? 'Organization' : 'Members';

  useEffect(() => {
    // If a sub route isn't specificed, push into /admin/members as the default route.
    if (pathname === PageUrls.ADMIN) {
      history.push(PageUrls.ADMIN_MEMBERS);
    }
  }, []);

  if (!isOrganizationAdmin) {
    history.push(PageUrls.HOME_PAGE);
  }

  return (
    <WpPageLayout width="container" className="OrgSettingsPage">
      <WpPageHeader>
        <WpPageTitle title="Admin Console" />
        <WpTabs>
          <WpTab to={PageUrls.ADMIN_MEMBERS}>{tabName}</WpTab>
          {showBillingTab && <WpTab to={PageUrls.ADMIN_BILLING}>Billing</WpTab>}
          {showIntegrationSettings && <WpTab to={PageUrls.ADMIN_INTEGRATIONS}>Integrations</WpTab>}
          <WpTab to={PageUrls.ADMIN_ANALYTICS}>Analytics</WpTab>
        </WpTabs>
      </WpPageHeader>
      <WpPageBody>
        <Switch>
          <Route exact path={PageUrls.ADMIN_MEMBERS} component={MembersManagement} />
          <Route exact path={PageUrls.ADMIN_BILLING} component={BillingPage} />
          <Route exact path={PageUrls.ADMIN_INTEGRATIONS} component={Enterprise} />
          <Route exact path={PageUrls.ADMIN_ANALYTICS} component={ExecutiveDashboardV2} />
          <Route exact path={PageUrls.ADMIN_NETWORK} component={NetworkVisualizer} />
        </Switch>
      </WpPageBody>
    </WpPageLayout>
  );
};

export default OrgSettingsPage;
